/* eslint-disable react/prop-types */
import classnames from "classnames";
import React from "react";
import CategoryFilterItem from "web/react/components/category-filter-item/category-filter-item";
import CheckboxFilterItem from "web/react/components/checkbox-filter-item/checkbox-filter-item";
import { FeedsFiltersPairOfSelects } from "web/react/components/feeds-filters/feeds-filters-pair-of-selects/feeds-filters-pair-of-selects";
import FilterOptionGroup from "web/react/components/filter-option-group/filter-option-group";
import { gettext } from "web/script/modules/django-i18n";
import environment from "web/script/modules/environment";
import {
    AllSelectedFilterInfo,
    SelectedFilterInfo,
    StandardFilterProps,
} from "web/types/feed-filters";
import {
    CategoryFilterOption,
    InputFilterOption,
    isFilterSection,
    UniversalFilterSection,
} from "web/types/universal-filters";
import ChevronFilterItem from "./chevron-filter-item";
import { PaginatedItem, PaginationExpansionState, PaginationProps } from "./pagination-state";
// This is only temporary disabled cause we are adding different css
// files depending on if the uj_tree_filter_all_screens flag
// is on or off.
// eslint-disable-next-line css-modules/no-unused-class
import styles1 from "./filter-pagination.css";
// eslint-disable-next-line css-modules/no-unused-class
import styles2 from "./filter-pagination2.css";

interface PaginationItemProps extends StandardFilterProps, PaginationProps {
    item: PaginatedItem;
    selectedFilterInfo: SelectedFilterInfo;
    lazyFacetFetch(optionGroupId: string): void;
    lazyFacetIsLoading: boolean;
}

function PaginationItemChildren({
    item,
    onPageChange,
    paginationState,
    ...props
}: PaginationItemProps): JSX.Element {
    let children: (CategoryFilterOption | InputFilterOption)[];

    let treeStructure = environment.getFeature("uj_tree_filter_all_screens");
    let styles = treeStructure ? styles2 : styles1;

    if (item.type == "category") {
        children = item.children;
    } else {
        if (item.type == "pair_of_selects") {
            return <FeedsFiltersPairOfSelects section={item} {...props} />;
        }
        let optionGroup = item.option_groups[0];
        if (optionGroup.type == "filtered_scroll_area") {
            return (
                <FilterOptionGroup
                    isVisible={
                        paginationState.getExpansionState(item) ==
                        PaginationExpansionState.ExpandedActive
                    }
                    {...{ optionGroup, ...props }}
                />
            );
        }
        children = optionGroup.options;
    }

    let reactChildren = children.map((item) => {
        let key = item.name + "=" + item.value.toString();
        if (item.type == "category" && item.name == "category") {
            return <CategoryFilterItem {...{ item, key, ...props }} />;
        } else if (item.type == "category") {
            return (
                <PaginationItem
                    {...{
                        item,
                        key,
                        onPageChange,
                        paginationState,
                        ...props,
                    }}
                />
            );
        } else {
            return (
                <li key={key}>
                    <CheckboxFilterItem {...{ item, ...props }} />
                </li>
            );
        }
    });

    let hasChildren = children.some(
        (item) =>
            item.type == "category" &&
            item.children.length > 0 &&
            props.pendingFilterInfo.getChecked(item)
    );
    return (
        <ol className={classnames(styles.children, { [styles.subChildOpen]: hasChildren })}>
            {reactChildren}
        </ol>
    );
}

function PaginationItem({
    item,
    onPageChange,
    paginationState,
    ...props
}: PaginationItemProps): JSX.Element {
    let expanded = paginationState.getExpansionState(item);
    let isSection = isFilterSection(item);

    let treeStructure = environment.getFeature("uj_tree_filter_all_screens");
    let styles = treeStructure ? styles2 : styles1;

    function getSubText(): string | undefined {
        if (!props.selectedFilterInfo) {
            return;
        }
        if (item.type == "pair_of_selects") {
            let crumb = props.selectedFilterInfo.breadcrumbs[0];
            if (crumb) {
                return `- ${crumb.label}`;
            }
        } else if (isSection) {
            let breadcrumbs = props.selectedFilterInfo.breadcrumbs;

            if (breadcrumbs.length == 0) {
                return;
            }

            if (breadcrumbs[0].name != "product_type") {
                return breadcrumbs.map((item) => `- ${item.label}`).join(" ");
            }

            let lastBreadcrumb = breadcrumbs[breadcrumbs.length - 1];
            if (lastBreadcrumb.type == "category" && lastBreadcrumb.children.length != 0) {
                // "All {category_name}"
                return `- ${gettext("feed.filters.all_categories_tooltip", {
                    category_name: lastBreadcrumb.label,
                })}`;
            } else if (lastBreadcrumb.type == "category" && lastBreadcrumb.children.length == 0) {
                return `- ${lastBreadcrumb.label}`;
            }

            return breadcrumbs
                .map((item) => {
                    if (item.type == "checkbox" || item.type == "radio") {
                        return `- ${item.label}`;
                    }
                })
                .filter((item) => item)
                .join(" ");
        } else if (item.type == "category") {
            let count = props.selectedFilterInfo.counts[item.name + "=" + item.value];
            if (count) {
                return `- ${gettext("feed.filters.mobile_selected_count", {
                    count: count,
                })}`;
            }
        }
    }

    let expandableId = "paginated_item_" + paginationState.getKey(item);

    return (
        <li
            className={classnames(styles.paginationItem, {
                [styles.sectionItem]: isSection,
            })}
        >
            <ChevronFilterItem
                item={item}
                large={isSection}
                onClick={onPageChange}
                expanded={expanded != PaginationExpansionState.Collapsed}
                subText={getSubText()}
                ariaControls={expandableId}
            />
            <div
                className={classnames(styles.expandable, {
                    [styles.expandedActive]: expanded == PaginationExpansionState.ExpandedActive,
                    [styles.expandedInactive]:
                        expanded == PaginationExpansionState.ExpandedInactive,
                    [styles.collapsed]: expanded == PaginationExpansionState.Collapsed,
                })}
                id={expandableId}
            >
                <PaginationItemChildren {...{ item, onPageChange, paginationState, ...props }} />
            </div>
        </li>
    );
}

interface FilterPaginationProps extends StandardFilterProps, PaginationProps {
    allSelectedFilterInfo: AllSelectedFilterInfo;
    sections: UniversalFilterSection[];
    lazyFacetFetch(optionGroupId: string): void;
    lazyFacetIsLoading: boolean;
}

const FilterPagination = React.forwardRef<HTMLOListElement, FilterPaginationProps>(
    function FilterPagination(
        { allSelectedFilterInfo, onPageChange, sections, ...props },
        ref
    ): JSX.Element {
        let treeStructure = environment.getFeature("uj_tree_filter_all_screens");
        let styles = treeStructure ? styles2 : styles1;

        let hasExpandedChildren = props.paginationState.hasActivePage();

        return (
            <div
                className={classnames(styles.container, {
                    [styles.expandedActive]: !hasExpandedChildren,
                    [styles.expandedInactive]: hasExpandedChildren,
                })}
            >
                <ol className={styles.children} ref={ref}>
                    {sections.map((section, index) => (
                        <PaginationItem
                            key={section.id}
                            item={section}
                            onPageChange={(item): void => onPageChange(item, index)}
                            selectedFilterInfo={allSelectedFilterInfo[section.id]}
                            {...props}
                        />
                    ))}
                </ol>
            </div>
        );
    }
);

export default FilterPagination;
